import {
  AMBIANCE_ASSET_RULES,
  type AmbianceAsset,
  type AmbianceAssetKind,
} from '@matter_extractor/shared';

/**
 * The words of the media library (immersion §6, I3), reduced by the cut to what
 * the file library still says about itself (issue #32).
 *
 * This file spoke for the Ambiance screen: the server's refusals, the slots,
 * their nature, the source that declares them, the removal warning. The slots,
 * the cues, the decors and the assignments leave with the cut (§17); the media
 * library stays - code, upload, metadata, file service - and restarts from an
 * empty library (§16, H7).
 *
 * What remains here is exactly what `media-picker.tsx` mounts: what the native
 * file picker accepts, and the line under a file's name. The rest comes back
 * with the screen that reads it, not before - a sentence kept « just in case »
 * is a sentence nobody re-reads.
 */

/**
 * What the native picker offers by nature, and for a choice that takes both.
 *
 * The server stays the authority (BUILD-683) - this only spares the operator a
 * round trip - and the list comes from `AMBIANCE_ASSET_RULES`, so the filter
 * cannot diverge from the check. `.jpeg` is added by hand: the shared table
 * names the CANONICAL format, and the disk writes it both ways.
 */
const IMAGE_ACCEPT = `${AMBIANCE_ASSET_RULES.image.formats.map((format) => `.${format}`).join(',')},.jpeg`;
const SOUND_ACCEPT = AMBIANCE_ASSET_RULES.sound.formats.map((format) => `.${format}`).join(',');

export const ASSET_ACCEPT: Record<AmbianceAssetKind | 'all', string> = {
  sound: SOUND_ACCEPT,
  image: IMAGE_ACCEPT,
  all: `${SOUND_ACCEPT},${IMAGE_ACCEPT}`,
};

/** `1:24` for eighty-four seconds - the minute:second a person reads. */
export function assetDuration(durationMs: number): string {
  const total = Math.round(durationMs / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * The line under a file's name in the library: what the file's header said
 * about it (BUILD-831), then the operator's words.
 *
 * A field the reader could not read is OMITTED rather than shown as a dash: an
 * image of unknown size is still a perfectly good image, and writing « ? x ? »
 * would read as a defect of the file rather than of the reader. The empty
 * string is therefore a legitimate answer, and every caller handles it.
 */
export function assetSubtitle(asset: AmbianceAsset): string {
  const parts: string[] = [];
  if (asset.width !== null && asset.height !== null) {
    parts.push(`${asset.width} x ${asset.height}`);
  }
  if (asset.durationMs !== null) parts.push(assetDuration(asset.durationMs));
  if (asset.tags.length > 0) parts.push(asset.tags.join(', '));
  return parts.join(' · ');
}
